import { ApiConstantsInterface, apiConstants } from '../constants/api.customer-orders.constants'
import Axios, { AxiosInstance } from 'axios'

import { CustomerOrdersInterface } from '../interfaces/customer-orders.interface'
import { CustomerOrdersResultsInterface } from '../interfaces/customer-orders-results.interface'
import { OrderDetailsInterface } from '../interfaces/order-details.interface'
import numeral from 'numeral'

declare global {
  interface Window {
    storeSettings: any
  }
}

window.storeSettings = window.storeSettings || {}

// TODO: REMOVE WHEN TEST DATA IS NOT NEEDED
// import SampleOrderDetail from '../test-data/order-details.json'
// import SampleOrders from '../test-data/orders.json'

interface CustomerOrdersServiceInterface {
  getCustomerOrders(): Promise<CustomerOrdersResultsInterface>
  getOrderDetails(id: number): Promise<OrderDetailsInterface>
}

class CustomerOrdersService implements CustomerOrdersServiceInterface {
  protected axios: AxiosInstance

  protected config: ApiConstantsInterface

  constructor(config: ApiConstantsInterface) {
    this.config = config
    const apiBaseUrl = process.env.VUE_APP_API_BASE_URL ?? ''
    this.axios = Axios.create({
      baseURL: `${apiBaseUrl}${config.baseUrl}`,
      withCredentials: true,
    })
  }

  async getCustomerOrders(): Promise<any> {
    const customerInfoResponse = await this.axios.get(this.config.getCustomerOrders)
    return this.convertOrdersInfoResponse(customerInfoResponse.data)
  }

  async getOrderDetails(id: number): Promise<OrderDetailsInterface> {
    const customerInfoResponse = await this.axios.get(this.config.getOrderDetails(id))

    return this.convertOrderDetailsResponse(customerInfoResponse.data)
  }

  convertOrdersInfoResponse(data: any): any[] {
    // Prepend the order prefix from Settings
    const prefix =
      window.storeSettings && window.storeSettings.OrderSettings
        ? window.storeSettings.OrderSettings.OrderIdPrefix || ''
        : ''
    const orderPrefix = prefix ? `${prefix}` : ''

    const ordersInfo = data.Orders.map((order) => {
      return {
        CreatedOn: order.CreatedOn,
        Id: order.Id,
        IdWithPrefix: `${orderPrefix}${order.Id}`,
        OrderStatus: order.OrderStatus,
        OrderTotal: order.OrderTotal,
      }
    })
    return ordersInfo
  }

  convertOrderDetailsResponse(data: any): OrderDetailsInterface {
    if (data) {
      const orderDetailsItems = data.Items.map((item) => {
        const splitAttributes = item.AttributeInfo ? item.AttributeInfo.split('<br />') : []
        const attributeObjects = splitAttributes.map((attr) => {
          const attrParts = attr.split(':')
          // Bugfix TCW-2388 check if attribute is Time and reassemble time splits
          if (attrParts[0] === 'Time' && attrParts.length > 2) {
            let time = ''
            for (let i = 1; i < attrParts.length; i++) {
              time += attrParts[i]
              if (i < attrParts.length - 1) {
                time += ':'
              }
            }
            attrParts[1] = time
            attrParts.length = 2
          }
          const attrName = attrParts.length < 2 ? '' : attrParts[0]
          let attrValue = attrParts.length < 2 ? '' : attrParts[1]
          // Some attribute values use pipes for descriptions. If those exist, remove the portion to the right of the pipe
          const attrValueParts = attrValue.split('|')
          attrValue = attrValueParts[0].trim()
          const isAssignedTo = attrName.toUpperCase() === 'ASSIGNEDTO'
          const isAddOn = attrName.includes('[+')

          // If the attribute value is a date, format it as a short date based on locale
          const isDate = !!Date.parse(attrValue)
          if (isDate) {
            const locale = window.storeSettings && window.storeSettings.Locale ? window.storeSettings.Locale : 'en-US'
            attrValue = new Intl.DateTimeFormat(locale).format(new Date(attrValue))
          }

          return {
            IsAddOn: isAddOn,
            IsAssignedTo: isAssignedTo,
            IsDate: isDate,
            IsHidden: false, // TODO: read the hidden attributes setting to determine visibility of attribute
            Name: attrName,
            Value: attrValue.trim(),
          }
        })

        // Calculate discounts
        const ItemUnitSubtotal = Number(item.SubTotal.replace(/[^0-9\.]+/g, ''))
        const ItemUnitPrice = Number(item.UnitPrice.replace(/[^0-9\.]+/g, ''))
        const UnitPriceWithDiscount = ItemUnitSubtotal / item.Quantity
        const DiscountAmount = ItemUnitPrice - UnitPriceWithDiscount
        const DiscountPercent = Math.abs((DiscountAmount / ItemUnitPrice) * 100)

        return {
          Attributes: attributeObjects,
          CustomProperties: item.CustomProperties,
          DiscountAmount: `${numeral(DiscountAmount).format('$0,0.00')}`,
          DiscountPercent: `${DiscountPercent}%`,
          Id: item.Id,
          ProductName: item.ProductName,
          ProductSeName: item.ProductSeName,
          Quantity: item.Quantity,
          Sku: item.Sku,
          SubTotal: item.SubTotal,
          UnitPrice: item.UnitPrice,
          UnitPriceValue: Number(item.UnitPrice.replace(/[^0-9\.]+/g, '')),
          UnitPriceWithDiscount: `${numeral(UnitPriceWithDiscount).format('$0,0.00')}`,
        }
      })

      // Use an order total less than a subtotal to indicate that a discount or additional payment method
      // like rewards points, vouchers or a gift card was applied.
      const OrderSubtotal = Number(data.OrderSubtotal.replace(/[^0-9\.]+/g, ''))
      const OrderTax = data.Tax ? Number(data.Tax.replace(/[^0-9\.]+/g, '')) : 0
      const CalculatedOrderTotal = parseFloat((OrderSubtotal + OrderTax).toFixed(2))
      const OrderTotal = Number(data.OrderTotal.replace(/[^0-9\.]+/g, ''))
      let OrderDiscountsAndPaymentsValue: string | null = null
      if (OrderTotal < CalculatedOrderTotal) {
        let OrderDiscountsAndPaymentsAmt = CalculatedOrderTotal - OrderTotal
        if (data.PricesIncludeTax) {
          OrderDiscountsAndPaymentsAmt = OrderDiscountsAndPaymentsAmt - data.Tax
        }

        OrderDiscountsAndPaymentsValue =
          OrderDiscountsAndPaymentsAmt > 0 ? `(${numeral(OrderDiscountsAndPaymentsAmt).format('$0,0.00')})` : ''
      }
      // Prepend the order prefix from Settings
      const prefix =
        window.storeSettings && window.storeSettings.OrderSettings
          ? window.storeSettings.OrderSettings.OrderIdPrefix || ''
          : ''
      const orderPrefix = prefix ? `${prefix}` : ''

      return {
        BillingAddress: data.BillingAddress,
        CreatedOn: data.CreatedOn,
        Id: data.Id,
        IdWithPrefix: `${orderPrefix}${data.Id}`,
        Items: orderDetailsItems || data.Items,
        OrderShipping: data.OrderShipping,
        OrderStatus: data.OrderStatus,
        OrderSubTotalDiscount: data.OrderSubTotalDiscount,
        OrderSubTotal: data.OrderSubtotal,
        OrderTotal: data.OrderTotal,
        OrderTotalDiscount: data.OrderTotalDiscount,
        OrderDiscountsAndPayments: OrderDiscountsAndPaymentsValue,
        PaymentMethod: data.PaymentMethod,
        Tax: data.Tax,
        TaxRates: data.TaxRates,
      }
    }

    // data is undefined or null, so return the unformatted data
    return data
  }
}

const customerOrdersService = new CustomerOrdersService(apiConstants)
export { customerOrdersService }
